import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

const dracoLoader = new DRACOLoader();
const gltfLoader  = new GLTFLoader();
const rgbeLoader  = new RGBELoader();
let loaderReady   = false;

function initLoader() {
    if (loaderReady) return;
    dracoLoader.setDecoderPath(window.VendorData.themeUrl + '/dist/draco/');
    gltfLoader.setDRACOLoader(dracoLoader);
    loaderReady = true;
}

async function preloadWireModels() {
    const wires = Object.values( VendorData.products.wires );
    for ( const wire of wires ) {
        if ( wire.model_url ) {
            Preview.loadGLTF( wire.model_url ).catch( () => {} );
        }
        if ( wire.wire_edge_url ) {
            Preview.loadGLTF( wire.wire_edge_url ).catch( () => {} );
        }
    }
}

/* Start preloading after page is idle */
window.addEventListener( 'load', () => {
    setTimeout( preloadWireModels, 2000 );
} );

const WORLD_SCALE = 26 / 18.91;

class WireScene {

    constructor() {
        this.scene       = null;
        this.camera      = null;
        this.renderer    = null;
        this.controls    = null;
        this.models      = {};
        this.animationId = null;
        this.wireBounds  = null;
        this.debug       = false;
        this.shrinkMeta = null;
        this.gltfCache = null;
        this.currentSelection = null;
        this.shrinkMeta = this.shrinkMeta ?? {};
        this.debugState  = {
            hsScale:   1.0,
            hsRx:      13,
            hsRy:      0,
            hsRz:      0,
            hsOverlap: 0.1,
            hsOffset:  0,
            lugRx:     13,
            lugRy:     0,
            lugRz:     0,
        };
    }

    init(containerId, options = {}) {
        const container = document.getElementById(containerId);
        if (!container) return;

        const width  = container.clientWidth;
        const height = container.clientHeight;

        this.scene = new THREE.Scene();
        this.debug = options.debug ?? false;

        // Orthographic camera
        const aspect   = width / height;
        const frustum  = 5;
        this.camera    = new THREE.OrthographicCamera(
            -frustum * aspect / 2,
             frustum * aspect / 2,
             frustum / 2,
            -frustum / 2,
            0.1,
            1000
        );

        // Position at 45 degrees, offset as per diagram
        this.camera.position.set(0, 18.75, 10);
        this.camera.lookAt(0, 0, 0);

        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
        this.renderer.setClearColor(0x000000, 0);
        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type    = THREE.PCFSoftShadowMap;
        this.renderer.toneMapping          = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure  = 1.4;
        this.renderer.outputColorSpace     = THREE.SRGBColorSpace;
        container.appendChild(this.renderer.domElement);

        this.controls               = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.enablePan     = false;
        this.controls.minDistance   = 3;
        this.controls.maxDistance   = 20;
        this.controls.enabled       = options.controls ?? false;
        this.controls.enableRotate  = options.controls ?? false;
        this.controls.enableZoom    = options.controls ?? false;

        // Directional light 1 — top, casts shadows, -72 degrees
        const dirLight1 = new THREE.DirectionalLight(0xffffff, 1.5);
        dirLight1.position.set(0, 3, 0);
        dirLight1.castShadow             = true;
        dirLight1.shadow.mapSize.width   = 2048;
        dirLight1.shadow.mapSize.height  = 2048;
        dirLight1.shadow.camera.near     = 0.1;
        dirLight1.shadow.camera.far      = 50;
        // Rotate -72 degrees on X axis
        dirLight1.position.applyEuler(new THREE.Euler(-72 * Math.PI / 180, 0, 0));
        this.scene.add(dirLight1);

        // Directional light 2 — bottom, no shadows, intensity 200
        const dirLight2 = new THREE.DirectionalLight(0xffffff, 0);
        dirLight2.position.set(0, -3, 0);
        dirLight2.castShadow = false;
        this.scene.add(dirLight2);

        // HDR environment map
        this.loadHDR();

        if (options.debugUI) {
            this.initDebugUI(container);
        }

        window.addEventListener('resize', () => this.onResize(container));
        
        
        this.animate();
        
        
    }

    fitCameraToScene() {
        const box = new THREE.Box3();

        Object.values(this.models).forEach(model => {
            if (!model.name?.startsWith('debug')) {
                box.expandByObject(model);
            }
        });

        if (box.isEmpty()) return;

        const size    = box.getSize(new THREE.Vector3());
        const center  = box.getCenter(new THREE.Vector3());
        const container = this.renderer.domElement.parentElement;
        const aspect  = container.clientWidth / container.clientHeight;

        // Add padding
        const padding  = 1.3;
        const frustumH = (size.y * padding);
        const frustumW = (size.x * padding) / aspect;
        const frustum  = Math.max(frustumH, frustumW);

        this.camera.left   = -frustum * aspect / 2;
        this.camera.right  =  frustum * aspect / 2;
        this.camera.top    =  frustum / 2;
        this.camera.bottom = -frustum / 2;
        this.camera.lookAt(center);
        this.camera.updateProjectionMatrix();
    }

    loadHDR() {
        const hdrUrl = window.VendorData.themeUrl + '/dist/hdr/hdr-studio_24_web.hdr';
        rgbeLoader.load(
            hdrUrl,
            (texture) => {
                texture.mapping        = THREE.EquirectangularReflectionMapping;
                this.scene.environment = texture;
            },
            null,
            (error) => console.error('Failed to load HDR:', error)
        );
    }

    loadGLTFOld(url) {
        if (!loaderReady) initLoader();
        return new Promise((resolve, reject) => {
            gltfLoader.load(
                url,
                (gltf) => {
                    const model  = gltf.scene;
                    const box    = new THREE.Box3().setFromObject(model);
                    const size   = box.getSize(new THREE.Vector3());
                    const center = box.getCenter(new THREE.Vector3());
                    resolve({ model, box, size, center });
                },
                null,
                (error) => reject(error)
            );
        });
    } 

    buildGroup(model, center, scale) {
        const group = new THREE.Group();
        model.position.set(-center.x, -center.y, -center.z);
        group.scale.setScalar(scale);
        group.add(model);
        return group;
    }

    addModel(key, mesh) {
        if (this.models[key]) this.removeModel(key);
        this.models[key] = mesh;
        this.scene.add(mesh);
    }

    removeModel(key) {
        if (!this.models[key]) return;
        this.scene.remove(this.models[key]);
        this.models[key].traverse((child) => {
            if (child.isMesh) {
                child.geometry?.dispose();
                if (Array.isArray(child.material)) {
                    child.material.forEach(m => m.dispose());
                } else {
                    child.material?.dispose();
                }
            }
        });
        delete this.models[key];
    }

    clearModels() {
        Object.keys(this.models).forEach(key => this.removeModel(key));
        this.wireBounds = null;
        this.currentSelection = null;
    }

    debugBox(key, color, minVec, maxVec) {
        if (!this.debug) return;
        const size   = new THREE.Vector3().subVectors(maxVec, minVec);
        const center = new THREE.Vector3().addVectors(minVec, maxVec).multiplyScalar(0.5);
        const geo    = new THREE.BoxGeometry(size.x, size.y, size.z);
        const mat    = new THREE.MeshBasicMaterial({ color, wireframe: true, transparent: true, opacity: 0.5 });
        const mesh   = new THREE.Mesh(geo, mat);
        mesh.position.copy(center);
        this.scene.add(mesh);
        this.models['debug_' + key] = mesh;
    }

    async loadDefault( url, edgeUrl = null, transparent = true ) {
        try {
            
            const { model, size, center } = await this.loadGLTF( url );
            const scale = 26 / Math.max( size.x, size.y, size.z );
            const group = this.buildGroup( model, center, scale );
            group.rotation.set( -0.3, 0, 0 );

            const halfX = ( size.x * scale ) / 2;
            const halfY = ( size.y * scale ) / 2;

            this.wireBounds = {
                leftX:  -halfX,
                rightX:  halfX,
                halfY:   halfY,
                transparent: transparent,
            };

            if ( transparent ) {
                group.traverse( child => {
                    if ( child.isMesh ) {
                        child.material.transparent = true;
                        child.material.opacity     = 0.3;
                    }
                } );
            }

            this.addModel( 'default', group );

            if ( edgeUrl ) {
                await this.placeWireEdge( 'left',  edgeUrl );
                await this.placeWireEdge( 'right', edgeUrl );
            }

            this.fitCameraToScene();
        } catch ( e ) {
            console.error( 'Failed to load default model:', e );
        }
    }

    async buildWireSet(selection) {
        this.clearModels();

        if (!selection.wireId) return;

        const wireData = VendorData.products.wires[selection.wireId];
        if (!wireData?.model_url) return;

        

        try {
            const { model, size, center } = await this.loadGLTF(wireData.model_url);
            const group = this.buildGroup(model, center, WORLD_SCALE);
            group.rotation.set(-0.3, 0, 0);

            const halfX = (size.x * WORLD_SCALE) / 2;
            const halfY = (size.y * WORLD_SCALE) / 2;

            this.wireBounds = {
                leftX:  -halfX,
                rightX:  halfX,
                halfY:   halfY,
            };

            this.addModel('wire', group);

            if (1 == 2 && this.debug) {
                this.debugBox('wire',
                    0xff0000,
                    new THREE.Vector3(-halfX, -halfY, -(size.z * WORLD_SCALE) / 2),
                    new THREE.Vector3( halfX,  halfY,  (size.z * WORLD_SCALE) / 2)
                );
            }

        } catch (e) {
            console.error('Failed to load wire:', e);
            return;
        }


        const edgeUrl  = wireData?.wire_edge_url ?? VendorData.defaultEdgeUrl ?? null;
        const rightEndType  = selection.rightEndType  ?? 'stripped-retained';
        const isBluntEnd    = rightEndType === 'blunt-end';
        const isStripped    = rightEndType === 'stripped';
        const strippedUrl   = wireData?.wire_edge_stripped_url  ?? null;

        if (selection.leftLugId) {
            await this.placeLug('left', selection.leftLugId);
        } else if (edgeUrl) {
            await this.placeWireEdge('left', edgeUrl);
        } 

        if (selection.rightLugId) {
            await this.placeLug('right', selection.rightLugId);
        } else if ( ! isBluntEnd ) {
            const rightEdgeUrl = isStripped && strippedUrl ? strippedUrl : edgeUrl;
            if ( rightEdgeUrl ) {
                await this.placeWireEdge( 'right', rightEdgeUrl );
            }
        }

        if (selection.leftShrinkId)  await this.placeHeatShrink('left',  selection.leftShrinkId, selection.leftLugId, selection.wireId);
        if (selection.rightShrinkId) await this.placeHeatShrink('right', selection.rightShrinkId, selection.leftLugId, selection.wireId);
        
        this.fitCameraToScene();
        this.refreshDebugUI();

    }

    /**
     * Add inside WireScene class in preview.js
     * Exploded view for PDF assembly page.
     * Wire + edges always shown. Heat shrinks and lugs separated with gaps.
     */

    async buildWireSetPDF( selection, pageData = null ) {
        this.clearModels();

        if ( ! selection.wireId ) return;

        const wireData = VendorData.products.wires[ selection.wireId ];
        if ( ! wireData?.model_url ) return;

        const GAP_SHRINK   = 1.2;
        const GAP_LUG      = 0.8;
        const lugRx        = 13 * Math.PI / 180;
        const edgeUrl      = wireData?.wire_edge_url          ?? VendorData.defaultEdgeUrl ?? null;
        const strippedUrl  = wireData?.wire_edge_stripped_url ?? null;
        const rightEndType = selection.rightEndType ?? 'stripped-retained';
        const isBluntEnd   = rightEndType === 'blunt-end';
        const isStripped   = rightEndType === 'stripped';

        const lugSame    = selection.leftLugId    && selection.rightLugId    && selection.leftLugId    === selection.rightLugId;
        const shrinkSame = selection.leftShrinkId && selection.rightShrinkId && selection.leftShrinkId === selection.rightShrinkId;

        /* Build item number map */
        let nextNum  = 1;
        const numMap = {};

        numMap.wire = nextNum++;

        if ( selection.leftShrinkId ) {
            numMap.shrinkLeft = nextNum;
            if ( shrinkSame ) numMap.shrinkRight = nextNum;
            nextNum++;
        }

        if ( selection.leftLugId ) {
            numMap.lugLeft = nextNum;
            if ( lugSame ) numMap.lugRight = nextNum;
            nextNum++;
        }

        if ( selection.rightShrinkId && ! shrinkSame ) numMap.shrinkRight = nextNum++;
        if ( selection.rightLugId    && ! lugSame    ) numMap.lugRight    = nextNum++;

        /* Wire */
        let wireHalfX = 0;

        try {
            const { model, size, center } = await this.loadGLTF( wireData.model_url );
            const group = this.buildGroup( model, center, WORLD_SCALE );
            group.rotation.set( -0.3, 0, 0 );

            wireHalfX = ( size.x * WORLD_SCALE ) / 2;

            this.wireBounds = {
                leftX:  -wireHalfX,
                rightX:  wireHalfX,
                halfY:   ( size.y * WORLD_SCALE ) / 2,
                sizeY:   size.y * WORLD_SCALE,
            };

            this.addModel( 'wire', group );
        } catch ( e ) {
            console.error( 'Failed to load wire:', e );
            return;
        }

        /* Wire edges */
        let edgeHalfX = 0;

        if ( edgeUrl ) {
            try {
                const { model: eModel, size: eSize, center: eCenter } = await this.loadGLTF( edgeUrl );
                edgeHalfX = ( eSize.x * WORLD_SCALE ) / 2;

                /* Left edge — always shown */
                const leftEdge = this.buildGroup( eModel.clone(), eCenter, WORLD_SCALE );
                leftEdge.position.x = this.wireBounds.leftX - edgeHalfX;
                leftEdge.rotation.set( lugRx, 0, 0 );
                this.addModel( 'edge_left', leftEdge );

                /* Right default edge — only for stripped-retained with no right lug and no custom stripped model */
                if ( ! selection.rightLugId && ! isBluntEnd && ! ( isStripped && strippedUrl ) ) {
                    const rightEdge = this.buildGroup( eModel.clone(), eCenter, WORLD_SCALE );
                    rightEdge.position.x = this.wireBounds.rightX + edgeHalfX;
                    rightEdge.rotation.set( -lugRx, Math.PI, 0 );
                    this.addModel( 'edge_right', rightEdge );
                }
            } catch ( e ) {
                console.error( 'Failed to load wire edge:', e );
            }
        }

        let cursorLeft  = this.wireBounds.leftX  - edgeHalfX * 2;
        let cursorRight = this.wireBounds.rightX + edgeHalfX * 2;

        /* Helper — resolve heatshrink model url using xrefs */
        const resolveShrinkUrl = ( shrinkId, lugId ) => {
            const shrinkData = VendorData.products.heatshrinks[ shrinkId ];
            const lugData    = lugId ? VendorData.products.lugs[ lugId ] : null;
            const lugXref    = lugData?.heat_shrink_models?.[ shrinkId ] ?? null;
            const wireXref   = shrinkData?.wire_shrink_models?.[ selection.wireId ] ?? null;
            const url        = lugXref ?? wireXref?.url ?? shrinkData?.model_url ?? null;
            const scale      = wireXref?.scale  ?? null;
            const offset     = wireXref?.offset ?? null;
            return { url, scale, offset };
        };

        /* Left heat shrink */
        if ( selection.leftShrinkId ) {
            const { url, scale: xrefScale } = resolveShrinkUrl( selection.leftShrinkId, selection.leftLugId );
            if ( url ) {
                try {
                    const { model, size, center } = await this.loadGLTF( url );
                    const hsScale = xrefScale ?? 1.0;
                    const group   = this.buildGroup( model, center, WORLD_SCALE );
                    group.scale.setScalar( hsScale );
                    const halfX   = ( size.x * WORLD_SCALE * hsScale ) / 2;
                    group.position.x = cursorLeft - GAP_SHRINK - halfX;
                    group.rotation.set( 0, 0, 0 );
                    this.addModel( 'shrink_left', group );
                    cursorLeft = group.position.x - halfX;
                } catch ( e ) {
                    console.error( 'Failed to load left shrink:', e );
                }
            }
        }

        /* Left lug */
        if ( selection.leftLugId ) {
            const lugData = VendorData.products.lugs[ selection.leftLugId ];
            if ( lugData?.model_url ) {
                try {
                    const { model, size, center } = await this.loadGLTF( lugData.model_url );
                    const rawScale = parseFloat( lugData?.custom_scale );
                    const lugScale = ( ! isNaN( rawScale ) && rawScale > 0 ) ? rawScale : 1.0;
                    const group    = this.buildGroup( model, center, WORLD_SCALE );
                    group.scale.setScalar( lugScale );
                    const halfX    = ( size.x * WORLD_SCALE * lugScale ) / 2;
                    group.position.x = cursorLeft - GAP_LUG - halfX;
                    group.rotation.set( lugRx, 0, 0 );
                    this.addModel( 'lug_left', group );
                } catch ( e ) {
                    console.error( 'Failed to load left lug:', e );
                }
            }
        }
        
        /* Right lug */
        if ( selection.rightLugId ) {
            const lugData = VendorData.products.lugs[ selection.rightLugId ];
            if ( lugData?.model_url ) {
                try {
                    
                    /* Right shrink first, then advance cursor, then lug uses gap from cursor */
                    if ( selection.rightShrinkId ) {
                        const { url, scale: xrefScale } = resolveShrinkUrl( selection.rightShrinkId, selection.rightLugId );
                        if ( url ) {
                            try {
                                const { model: sModel, size: sSize, center: sCenter } = await this.loadGLTF( url );
                                const hsScale = xrefScale ?? 1.0;
                                const sGroup  = this.buildGroup( sModel, sCenter, WORLD_SCALE );
                                sGroup.scale.setScalar( hsScale );
                                const sHalfX  = ( sSize.x * WORLD_SCALE * hsScale ) / 2;
                                sGroup.position.x = cursorRight - GAP_SHRINK + sHalfX;
                                sGroup.rotation.set( 0, Math.PI, 0 );
                                this.addModel( 'shrink_right', sGroup );
                                cursorRight = sGroup.position.x + sHalfX;
                            } catch ( e ) {
                                console.error( 'Failed to load right shrink:', e );
                            }
                        }
                    }

                    /* Right lug — placed after shrink with gap */
                    const { model, size, center } = await this.loadGLTF( lugData.model_url );
                    const rawScale = parseFloat( lugData?.custom_scale );
                    const lugScale = ( ! isNaN( rawScale ) && rawScale > 0 ) ? rawScale : 1.0;
                    const group    = this.buildGroup( model, center, WORLD_SCALE );
                    group.scale.setScalar( lugScale );
                    const halfX    = ( size.x * WORLD_SCALE * lugScale ) / 2;

                    if ( selection.rightShrinkId ) {
                        group.position.x = cursorRight + GAP_LUG + halfX;
                    } else {
                        group.position.x = cursorRight - GAP_LUG + halfX;    
                    }

                    
                    group.rotation.set( lugRx, Math.PI, 0 );
                    this.addModel( 'lug_right', group );

                } catch ( e ) {
                    console.error( 'Failed to load right lug:', e );
                }
            }
        } else if ( ! isBluntEnd ) {
            /* No right lug — load stripped or default edge */
            const rightEdgeUrl = isStripped && strippedUrl ? strippedUrl : null;
            if ( rightEdgeUrl ) {
                try {
                    const { model: rModel, size: rSize, center: rCenter } = await this.loadGLTF( rightEdgeUrl );
                    const rHalfX    = ( rSize.x * WORLD_SCALE ) / 2;
                    const rightEdge = this.buildGroup( rModel, rCenter, WORLD_SCALE );
                    rightEdge.position.x = this.wireBounds.rightX + rHalfX;
                    rightEdge.rotation.set( -lugRx, Math.PI, 0 );
                    this.addModel( 'edge_right', rightEdge );
                    cursorRight = rightEdge.position.x + rHalfX;
                } catch ( e ) {
                    console.error( 'Failed to load stripped edge:', e );
                }
            }
        }
        /* isBluntEnd — nothing on right side */

        this.fitCameraToScene();

        this.camera.position.set( -1.756, 19.386, 4.592 );
        this.camera.left   = -38.385;
        this.camera.right  =  38.385;
        this.camera.top    =  15.601;
        this.camera.bottom = -15.601;
        this.camera.lookAt( 0, 0, 0 );
        this.camera.updateProjectionMatrix();

        const centerOf = ( key ) => {
            const model = this.models[ key ];
            if ( ! model ) return null;
            return new THREE.Box3().setFromObject( model ).getCenter( new THREE.Vector3() );
        };

        const addLabel = ( modelKey, numKey ) => {
            const pos = centerOf( modelKey );
            if ( pos && numMap[ numKey ] !== undefined ) this.addLeaderLabel( pos, numMap[ numKey ] );
        };

        addLabel( 'wire',         'wire'        );
        addLabel( 'shrink_left',  'shrinkLeft'  );
        addLabel( 'shrink_right', 'shrinkRight' );
        addLabel( 'lug_left',     'lugLeft'     );
        addLabel( 'lug_right',    'lugRight'    );

        /* Wait two frames for sprites and labels to render */
        await new Promise( resolve => requestAnimationFrame( resolve ) );
        await new Promise( resolve => requestAnimationFrame( resolve ) );

        this.renderer.render( this.scene, this.camera );

        cancelAnimationFrame( this.animationId );
        this.animationId = null;
    }

    addLeaderLabel( worldPos, num ) {
        /* Circle + number texture */
        const size   = 128;
        const cvs    = document.createElement( 'canvas' );
        cvs.width    = size;
        cvs.height   = size;
        const ctx    = cvs.getContext( '2d' );

        ctx.beginPath();
        ctx.arc( size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2 );
        ctx.fillStyle   = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth   = 8;
        ctx.stroke();

        ctx.font         = 'bold 64px Arial';
        ctx.fillStyle    = '#000000';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText( String( num ), size / 2, size / 2 );

        const texture  = new THREE.CanvasTexture( cvs );
        const material = new THREE.SpriteMaterial( { map: texture, depthTest: false } );
        const sprite   = new THREE.Sprite( material );

        const labelOffset = 16;
        sprite.position.set( worldPos.x, worldPos.y - labelOffset, worldPos.z );
        sprite.scale.set( 2, 2, 1 );  // was 0.8

        this.scene.add( sprite );
        this.models[ 'label_' + num + '_' + Date.now() ] = sprite;

        /* Leader line */
        const points = [
            new THREE.Vector3( worldPos.x, worldPos.y, worldPos.z ),
            sprite.position.clone(),
        ];
        const geo  = new THREE.BufferGeometry().setFromPoints( points );
        const mat  = new THREE.LineBasicMaterial( { color: 0x000000, depthTest: false } );
        const line = new THREE.Line( geo, mat );
        this.scene.add( line );
        this.models[ 'line_' + num + '_' + Date.now() ] = line;
    }

    async placeWireEdge(side, url) {
        if (!this.wireBounds) return;

        try {
            const { model, size, center } = await this.loadGLTF(url);
            const group = this.buildGroup(model, center, WORLD_SCALE);

            const halfX = (size.x * WORLD_SCALE) / 2;
            const lugRx = (this.debugState?.lugRx ?? 13) * Math.PI / 180;

            if (side === 'left') {
                group.position.x = this.wireBounds.leftX - halfX;
                group.rotation.set(lugRx, 0, 0);
            } else {
                group.position.x = this.wireBounds.rightX + halfX;
                group.rotation.set(-lugRx, Math.PI, 0);
            }

            this.addModel('edge_' + side, group);

            if ( this.wireBounds?.transparent ) {
                group.traverse( child => {
                    if ( child.isMesh ) {
                        child.material.transparent = true;
                        child.material.opacity     = 0.3;
                    }
                } );
            }

        } catch (e) {
            console.error('Failed to load wire edge [' + side + ']:', e);
        }
    }

    async placeLug(side, lugId) {
        if (!this.wireBounds) return;

        
        const lugData = VendorData.products.lugs[lugId];
        if (!lugData?.model_url) return;

        const lugOffset = 0;

        const rawOffset = parseFloat( lugData?.custom_y_position );
        const lugYOffset = ( ! isNaN( rawOffset ) ) ? rawOffset : 0;
        

        try {
            const { model, size, center } = await this.loadGLTF(lugData.model_url);
            const group = this.buildGroup(model, center, WORLD_SCALE);

            const rawScale = parseFloat( lugData?.custom_scale );
            const lugScale = ( ! isNaN( rawScale ) && rawScale > 0 ) ? rawScale : 0.9;

            
            group.position.y = lugYOffset;
            //console.log( `lug [${side}] center:`, center, 'size:', size, 'position:', group.position );
            //console.log(lugScale);

            group.scale.setScalar( WORLD_SCALE * lugScale );
            const halfX    = ( size.x * WORLD_SCALE * lugScale ) / 2;
            
            const lugRx = (this.debugState?.lugRx ?? 13) * Math.PI / 180;
            

            if (side === 'left') {
                group.position.x = this.wireBounds.leftX - halfX - lugOffset;
                group.rotation.set(lugRx, 0, 0);
            } else {
                group.position.x = this.wireBounds.rightX + halfX + lugOffset;
                group.rotation.set(lugRx, Math.PI, 0);
            }

            this.addModel('lug_' + side, group);

            if (1 == 2 && this.debug) {
                const worldMinX = group.position.x - halfX;
                const worldMaxX = group.position.x + halfX;
                this.debugBox('lug_' + side,
                    0x00ff00,
                    new THREE.Vector3(worldMinX, -(size.y * WORLD_SCALE) / 2, -(size.z * WORLD_SCALE) / 2),
                    new THREE.Vector3(worldMaxX,  (size.y * WORLD_SCALE) / 2,  (size.z * WORLD_SCALE) / 2)
                );
            }

        } catch (e) {
            console.error('Failed to load lug [' + side + ']:', e);
        }
    }

    async placeHeatShrink(side, shrinkId, lugId = null, wireId = null) {
        

        if (!this.wireBounds) return;

        const shrinkData = VendorData.products.heatshrinks[shrinkId];
        const lugData  = lugId ? VendorData.products.lugs[ lugId ] : null;

        const xrefUrl          = lugData?.heat_shrink_models?.[ shrinkId ] ?? null;
        const wireXref    = wireId ? ( shrinkData?.wire_shrink_models?.[ wireId ] ?? null ) : null;
        const wireXrefUrl = wireXref?.url ?? null;
        const modelUrl    = xrefUrl ?? wireXrefUrl ?? shrinkData?.model_url;

        const modelSource = xrefUrl     ? 'lug xref'
                  : wireXrefUrl ? 'wire xref'
                  : 'default';
        //console.log( `placeHeatShrink [${side}] shrinkId:${shrinkId} source:${modelSource} url:${modelUrl}` );

        if (!modelUrl) return;

        

        try {
            const { model, size, center } = await this.loadGLTF(modelUrl);
            const group = this.buildGroup(model, center, WORLD_SCALE);

            const xrefData  = lugData?.heat_shrink_models_data?.[ shrinkId ] ?? null;

            

            //const hsScale   = xrefData?.hsscale   ?? wireXref?.scale            ?? 0.9;
            const hasCustomScale = ( xrefData?.hsscale != null ) || ( wireXref?.scale != null ) || ( shrinkData?.custom_scale != null );


            let hsScale;
            if ( hasCustomScale ) {
                hsScale = xrefData?.hsscale ?? wireXref?.scale ?? shrinkData?.custom_scale;
            } else if ( this.debugState?.hsScale !== undefined ) {
                hsScale = this.debugState.hsScale;
            } else {
                const wireThickness   = this.wireBounds?.sizeY ?? 1;
                const shrinkThickness = size.y * WORLD_SCALE;
                hsScale = shrinkThickness > 0 ? ( wireThickness * 1.1 ) / shrinkThickness : 1.0;
            }

            const hsRx      = ( xrefData?.hsrx    ?? this.debugState?.hsRx      ?? 13  ) * Math.PI / 180;
            const hsRy      = ( xrefData?.hsry    ?? this.debugState?.hsRy      ?? 0   ) * Math.PI / 180;
            const hsRz      = ( xrefData?.hsrz    ?? this.debugState?.hsRz      ?? 0   ) * Math.PI / 180;
            const hsOverlap = xrefData?.hsoverlap ?? wireXref?.offset ?? shrinkData?.custom_offset ?? 0.08;
            const hsOffset  = xrefData?.hsoffset  ?? this.debugState?.hsOffset  ?? 0;

            //console.log(hsScale);
            //console.log(hsOverlap);

            this.debugState.hsScale   = hsScale;
            this.debugState.hsOverlap = hsOverlap;
            this.debugState.hsOffset  = hsOffset;

            group.scale.setScalar(WORLD_SCALE * hsScale);

            const halfX  = (size.x * WORLD_SCALE * hsScale) / 2;
            const overlap = halfX * hsOverlap;

            if (side === 'left') {
                group.position.x = this.wireBounds.leftX + overlap + hsOffset;
                group.rotation.set(hsRx, hsRy, hsRz);
            } else {
                group.position.x = this.wireBounds.rightX - overlap - hsOffset;
                group.rotation.set(hsRx, Math.PI + hsRy, hsRz);
            }

            this.addModel('shrink_' + side, group);

            this.shrinkMeta[ side ] = {
                baseX: side === 'left' ? this.wireBounds.leftX : this.wireBounds.rightX,
                halfX,
                size,
                xrefData,
            };
         

            if (1 == 2 && this.debug) {
                const worldMinX = group.position.x - halfX;
                const worldMaxX = group.position.x + halfX;
                this.debugBox('shrink_' + side,
                    0xffff00,
                    new THREE.Vector3(worldMinX, -(size.y * WORLD_SCALE) / 2, -(size.z * WORLD_SCALE) / 2),
                    new THREE.Vector3(worldMaxX,  (size.y * WORLD_SCALE) / 2,  (size.z * WORLD_SCALE) / 2)
                );
            }

        } catch (e) {
            console.error('Failed to load heatshrink [' + side + ']:', e);
        }
    }

    animate() {
        this.animationId = requestAnimationFrame(() => this.animate());
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }

    onResize(container) {
        const width  = container.clientWidth;
        const height = container.clientHeight;

        if (this.camera.isOrthographicCamera) {
            const aspect  = width / height;
            const frustum = 5;
            this.camera.left   = -frustum * aspect / 2;
            this.camera.right  =  frustum * aspect / 2;
            this.camera.top    =  frustum / 2;
            this.camera.bottom = -frustum / 2;
        } else {
            this.camera.aspect = width / height;
        }

        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
        this.fitCameraToScene();
    }

    destroy() {
        cancelAnimationFrame(this.animationId);
        this.controls.dispose();
        this.renderer.dispose();
        this.clearModels();
    }

initDebugUI(container) {
    this.debugState = {
        cameraX:  0,
        cameraY:   18.75,
        cameraZ:   10,
        frustum:   5,
        light1Y:   0.927,
        light1Int: 1.5,
        light2Y:  -3,
        light2Int: 0,
        lugRx: 13,
        lugRy: 0,
        lugRz: 0,
        hsRx:      0,
        hsRy:      0,
        hsRz:      0,
        hsOverlap: 0.1,
        hsScale: 1,
        hsOffset: 0,
    };

    const ui = document.createElement('div');
    ui.id = 'scene-debug-ui';
    ui.style.cssText = `
        position: absolute;
        top: 10px;
        right: 10px;
        background: rgba(0,0,0,0.75);
        color: #fff;
        font: 12px monospace;
        padding: 12px;
        border-radius: 6px;
        z-index: 100;
        min-width: 420px;
        user-select: none;
    `;

    ui.innerHTML = this.buildDebugHTML();
    container.style.position = 'relative';
    container.appendChild(ui);

    this.debugUI = ui;
    this.bindDebugEvents(container);
}

refreshDebugUI() {
    if ( ! this.debugUI ) return;
    const update = ( id, val ) => {
        const input = this.debugUI.querySelector( '#dbg-' + id );
        const label = this.debugUI.querySelector( '#dbg-' + id + '-val' );
        if ( ! input || val === null || val === undefined ) return;
        input.value      = val;
        if ( label ) label.textContent = parseFloat( val ).toFixed( 2 );
    };

    update( 'hs-scale',   this.debugState.hsScale   );
    update( 'hs-overlap', this.debugState.hsOverlap );
    update( 'hs-offset',  this.debugState.hsOffset  );
}

buildDebugHTML() {
    const s = this.debugState;
    return `
        <div style="font-weight:bold;margin-bottom:8px;color:#aef">Scene Debug</div>

        <div style="margin:6px 0;color:#fa0">Heatshrink</div>
${ this.debugRow( 'hs-scale',   'Scale',     s.hsScale,   0.1, 3,   0.01 ) }
${ this.debugRow( 'hs-overlap', 'Overlap',   s.hsOverlap, -10, 10,  0.01 ) }
${ this.debugRow( 'hs-offset',  'Offset X',  s.hsOffset,  -10, 10,  0.01 ) }

        <div style="margin-top:10px;">
            <button id="dbg-copy" style="background:#333;color:#fff;border:1px solid #666;padding:4px 10px;cursor:pointer;border-radius:4px;">Copy Values</button>
        </div>
        <div id="dbg-output" style="margin-top:8px;color:#8f8;font-size:11px;"></div>
    `;
}

debugRow(id, label, value, min, max, step) {
    return `
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
            <span style="width:70px;color:#ccc">${label}</span>
            <input type="range" id="dbg-${id}" min="${min}" max="${max}" step="${step}" value="${value}"
                style="flex:1;accent-color:#fa0;">
            <span id="dbg-${id}-val" style="width:45px;text-align:right;color:#fff">${value}</span>
        </div>
    `;
}

bindDebugEvents( container ) {
    const ui  = this.debugUI;
    const get = ( id ) => parseFloat( ui.querySelector( '#dbg-' + id ).value );

    const applyChanges = () => {
        const hsScale   = get( 'hs-scale' );
        const hsOverlap = get( 'hs-overlap' );
        const hsOffset  = get( 'hs-offset' );

        this.debugState.hsScale   = hsScale;
        this.debugState.hsOverlap = hsOverlap;
        this.debugState.hsOffset  = hsOffset;

        [ 'left', 'right' ].forEach( side => {
            const key   = 'shrink_' + side;
            const model = this.models[ key ];
            const meta  = this.shrinkMeta?.[ side ];
            if ( ! model || ! meta ) return;

            const xrefData = meta.xrefData;

            /* Sliders always win during debug — xrefData only used for rotation */
            const activeRx = ( xrefData?.hsrx ?? this.debugState?.hsRx ?? 13 ) * Math.PI / 180;
            const activeRy = ( xrefData?.hsry ?? this.debugState?.hsRy ?? 0  ) * Math.PI / 180;
            const activeRz = ( xrefData?.hsrz ?? this.debugState?.hsRz ?? 0  ) * Math.PI / 180;

            const halfX   = ( meta.size.x * WORLD_SCALE * hsScale ) / 2;
            const overlap = halfX * hsOverlap;

            model.scale.setScalar( WORLD_SCALE * hsScale );

            if ( side === 'left' ) {
                model.position.x = meta.baseX + overlap + hsOffset;
                model.rotation.set( activeRx, activeRy, activeRz );
            } else {
                model.position.x = meta.baseX - overlap - hsOffset;
                model.rotation.set( activeRx, Math.PI + activeRy, activeRz );
            }
        } );
    };

    ui.querySelectorAll( 'input[type=range]' ).forEach( input => {
        input.addEventListener( 'input', () => {
            const valEl = ui.querySelector( '#' + input.id + '-val' );
            if ( valEl ) valEl.textContent = parseFloat( input.value ).toFixed( 2 );
            applyChanges();
        } );
    } );

    ui.querySelector( '#dbg-copy' ).addEventListener( 'click', () => {
        const vals = `hsscale: ${get( 'hs-scale' )};\nhsoverlap: ${get( 'hs-overlap' )};\nhsoffset: ${get( 'hs-offset' )};`;

        navigator.clipboard.writeText( vals ).then( () => {
            ui.querySelector( '#dbg-output' ).textContent = 'Copied!';
            setTimeout( () => {
                ui.querySelector( '#dbg-output' ).textContent = '';
            }, 2000 );
        } );
    } );
}


getPartScreenPositions() {
    const canvas  = this.renderer.domElement;
    const width   = canvas.width  / window.devicePixelRatio;
    const height  = canvas.height / window.devicePixelRatio;
    const results = {};
 
    const toScreen = ( worldPos ) => {
        const vec = worldPos.clone().project( this.camera );
        return {
            x: ( vec.x + 1 ) / 2 * width,
            y: ( 1 - ( vec.y + 1 ) / 2 ) * height,
        };
    };
 
    const centerOf = ( modelKey ) => {
        const model = this.models[ modelKey ];
        if ( ! model ) return null;
        const box = new THREE.Box3().setFromObject( model );
        return box.getCenter( new THREE.Vector3() );
    };
 
    if ( this.models['wire'] ) {
        results.wire = toScreen( centerOf( 'wire' ) );
    }
 
    /* Heat shrinks */
    const shrinkLCenter = centerOf( 'shrink_left' );
    const shrinkRCenter = centerOf( 'shrink_right' );
 
    if ( shrinkLCenter ) results.shrinkLeft  = toScreen( shrinkLCenter );
    if ( shrinkRCenter ) results.shrinkRight = toScreen( shrinkRCenter );
 
    /* Lugs */
    const lugLCenter = centerOf( 'lug_left' );
    const lugRCenter = centerOf( 'lug_right' );
 
    if ( lugLCenter ) results.lugLeft  = toScreen( lugLCenter );
    if ( lugRCenter ) results.lugRight = toScreen( lugRCenter );
 
    return results;
}


/* ------------------------------------------------------------------ */
/* GLB Cache                                                            */
/* ------------------------------------------------------------------ */

loadGLTF( url ) {
    if ( ! loaderReady ) initLoader();

    this.gltfCache = this.gltfCache ?? new Map();

    if ( this.gltfCache.has( url ) ) {
        const cached = this.gltfCache.get( url );
        const cloned = cached.model.clone();

        cloned.traverse( child => {
            if ( child.isMesh ) {
                child.material             = child.material.clone();
                child.material.transparent = false;
                child.material.opacity     = 1;
            }
        } );

        return Promise.resolve( {
            model:  cloned,
            size:   cached.size.clone(),
            center: cached.center.clone(),
        } );
    }

    return new Promise( ( resolve, reject ) => {
        gltfLoader.load(
            url,
            ( gltf ) => {
                const model  = gltf.scene;
                const box    = new THREE.Box3().setFromObject( model );
                const size   = box.getSize( new THREE.Vector3() );
                const center = box.getCenter( new THREE.Vector3() );

                this.gltfCache.set( url, {
                    model:  model.clone(),
                    size:   size.clone(),
                    center: center.clone(),
                } );

                resolve( { model, size, center } );
            },
            null,
            ( error ) => reject( error )
        );
    } );
}

/* ------------------------------------------------------------------ */
/* Incremental update                                                   */
/* ------------------------------------------------------------------ */

async updateWireSet( selection ) {
    const prev = this.currentSelection ?? {};
    const next = selection;

    /* Wire changed — full redraw */
    if ( prev.wireId !== next.wireId ) {
        this.currentSelection = { ...next };
        await this.buildWireSet( next );
        return;
    }

    /* Left lug changed */
    if ( prev.leftLugId !== next.leftLugId ) {
        this.removeModel( 'lug_left' );
        this.removeModel( 'shrink_left' );
        this.removeModel( 'edge_left' );

        const wireData = VendorData.products.wires[ next.wireId ];
        const edgeUrl  = wireData?.wire_edge_url ?? VendorData.defaultEdgeUrl ?? null;

        
        if ( next.leftLugId ) {
            await this.placeLug( 'left', next.leftLugId, next.leftShrinkId );
        } else if ( edgeUrl ) {
            await this.placeWireEdge( 'left', edgeUrl );
        }

        /* Shrink is now invalid — clear it from selection too */
        next.leftShrinkId = null;
        jQuery( '#leftshrink' ).val( '' );
    }

    console.log(prev.rightLugId);
    console.log(next.rightLugId);

    /* Right lug changed */
    if ( prev.rightLugId !== next.rightLugId ) {
        this.removeModel( 'lug_right' );
        this.removeModel( 'shrink_right' );
        this.removeModel( 'edge_right' );

        const wireData = VendorData.products.wires[ next.wireId ];
        const edgeUrl  = wireData?.wire_edge_url ?? VendorData.defaultEdgeUrl ?? null;
        const strippedUrl   = wireData?.wire_edge_stripped_url ?? null;
        const rightEndType  = selection.rightEndType ?? 'stripped-retained';
        const isBluntEnd    = rightEndType === 'blunt-end';
        const isStripped    = rightEndType === 'stripped';

        console.log(rightEndType);
        console.log(next.rightLugId);

        if ( next.rightLugId ) {
            await this.placeLug( 'right', next.rightLugId, next.rightShrinkId );
        } else if ( ! isBluntEnd ) {
            const rightEdgeUrl = isStripped && strippedUrl ? strippedUrl : edgeUrl;

            if ( rightEdgeUrl ) {
                await this.placeWireEdge( 'right', rightEdgeUrl );
            }
        }

        next.rightShrinkId = null;
        jQuery( '#rightshrink' ).val( '' );
    }

    /* Left shrink changed */
    if ( prev.leftShrinkId !== next.leftShrinkId ) {
        this.removeModel( 'shrink_left' );

        if ( next.leftShrinkId ) {
            await this.placeHeatShrink( 'left', next.leftShrinkId, next.leftLugId, next.wireId );
        }
    }

    /* Right shrink changed */
    if ( prev.rightShrinkId !== next.rightShrinkId ) {
        this.removeModel( 'shrink_right' );

        if ( next.rightShrinkId ) {
            await this.placeHeatShrink( 'right', next.rightShrinkId, next.rightLugId, next.wireId );
        }
    }

    this.currentSelection = { ...next };
    this.fitCameraToScene();
    this.refreshDebugUI();

}


} //end class



const Preview = new WireScene();

export { WireScene, initLoader };
export default Preview;